const fvRoutes = window.flexvibeRoutes || {};
const fvEscapeHtml = (value = "") => {
  const element = document.createElement("div");
  element.textContent = String(value);
  return element.innerHTML;
};

class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll("[data-close-cart]").forEach((button) => {
      button.addEventListener("click", () => this.close());
    });

    document.addEventListener("flexvibe:cart-open", () => this.open());
  }

  open() {
    this.previouslyFocused = document.activeElement;
    this.classList.add("is-open");
    document.body.style.overflow = "hidden";
    this.querySelector("[data-close-cart]")?.focus();
  }

  close() {
    this.classList.remove("is-open");
    document.body.style.overflow = "";
    this.previouslyFocused?.focus();
  }
}

if (!customElements.get("cart-drawer")) {
  customElements.define("cart-drawer", CartDrawer);
}

const refreshCart = async () => {
  try {
    const response = await fetch(`${fvRoutes.cart || "/cart"}.js`);
    if (!response.ok) throw new Error("Unable to load cart");
    const cart = await response.json();

    document.querySelectorAll("[data-cart-count]").forEach((element) => {
      element.textContent = cart.item_count;
    });

    document.querySelectorAll("[data-cart-total]").forEach((element) => {
      element.textContent = (cart.total_price / 100).toLocaleString(undefined, {
        style: "currency",
        currency: cart.currency || "USD",
      });
    });

    document.querySelectorAll("[data-cart-footer]").forEach((element) => {
      element.hidden = cart.item_count === 0;
    });

    const cartHasPreorder = cart.items.some((item) => {
      const properties = item.properties || {};
      return properties["_FlexVibe preorder"] === "Yes";
    });

    document.querySelectorAll("[data-preorder-cart-notice]").forEach((element) => {
      element.hidden = !cartHasPreorder;
    });

    document.querySelectorAll("[data-cart-policy-label]").forEach((element) => {
      element.textContent = cartHasPreorder
        ? "I understand the final-sale terms, including the preorder payment and cancellation terms, subject to the stated exceptions."
        : "I understand the final-sale terms, subject to the stated exceptions.";
    });

    document.querySelectorAll("[data-shipping-progress]").forEach((element) => {
      const threshold = Number(element.dataset.threshold || 0);
      if (!threshold) return;
      const remaining = Math.max(0, threshold - cart.total_price);
      const percent = Math.min(100, (cart.total_price / threshold) * 100);
      const message = element.querySelector("[data-shipping-message]");
      const fill = element.querySelector("[data-shipping-fill]");
      if (fill) fill.style.width = `${percent}%`;
      if (message) {
        message.textContent = remaining > 0
          ? `Add ${(remaining / 100).toLocaleString(undefined, { style: "currency", currency: cart.currency || "USD" })} more for free shipping.`
          : "You unlocked free shipping!";
      }
    });

    const box = document.querySelector("[data-cart-lines]");

    if (box) {
      box.innerHTML =
        cart.items
          .map((item) => {
            return `
              <div class="cart-line">
                <a href="${fvEscapeHtml(item.url || "#")}"><img src="${fvEscapeHtml(item.image || "")}" alt="${fvEscapeHtml(item.product_title)}"></a>
                <div>
                  <a href="${fvEscapeHtml(item.url || "#")}"><strong>${fvEscapeHtml(item.product_title)}</strong></a><br>
                  ${item.variant_title && item.variant_title !== "Default Title" ? `<small>${fvEscapeHtml(item.variant_title)}</small><br>` : ""}
                  <small>${(item.final_line_price / 100).toLocaleString(undefined, {
                    style: "currency",
                    currency: cart.currency || "USD",
                  })}</small>
                  <div class="cart-line-actions">
                    <button type="button" data-cart-quantity="${Math.max(0, item.quantity - 1)}" data-line-key="${fvEscapeHtml(item.key)}" aria-label="Decrease ${fvEscapeHtml(item.product_title)} quantity">−</button>
                    <span aria-label="Quantity">${item.quantity}</span>
                    <button type="button" data-cart-quantity="${item.quantity + 1}" data-line-key="${fvEscapeHtml(item.key)}" aria-label="Increase ${fvEscapeHtml(item.product_title)} quantity">+</button>
                    <button type="button" data-cart-quantity="0" data-line-key="${fvEscapeHtml(item.key)}">Remove</button>
                  </div>
                </div>
              </div>
            `;
          })
          .join("") || `<div class="fv-empty-cart"><p>Your cart is ready for its first pull.</p><a class="btn full" href="${fvEscapeHtml(fvRoutes.allProducts || "/collections/all")}">Shop products</a></div>`;
    }
  } catch (error) {
    console.warn(error);
  }
};

const toggleMobileNav = (open) => {
  const nav = document.querySelector("[data-mobile-nav]");

  if (!nav) return;

  nav.hidden = !open;
};

document.addEventListener("click", async (event) => {
  const openCart = event.target.closest("[data-open-cart]");

  if (openCart) {
    event.preventDefault();
    await refreshCart();
    document.dispatchEvent(new Event("flexvibe:cart-open"));
    return;
  }

  const openMenu = event.target.closest("[data-open-menu]");

  if (openMenu) {
    event.preventDefault();
    toggleMobileNav(true);
    return;
  }

  const closeMenu = event.target.closest("[data-close-menu]");

  if (closeMenu) {
    event.preventDefault();
    toggleMobileNav(false);
    return;
  }

  const add = event.target.closest("[data-ajax-add]");

  if (add) {
    event.preventDefault();

    const form = add.closest("form");

    if (!form) return;

    add.disabled = true;
    add.textContent = "Adding...";

    try {
      const response = await fetch(`${fvRoutes.cartAdd || "/cart/add"}.js`, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || "Could not add item");
      }

      await refreshCart();
      add.classList.add("fv-caught");
      const card = add.closest(".fv-card, .fv-buy-box, .fv-quick-card");
      card?.classList.add("fv-card-caught");
      window.setTimeout(() => {
        add.classList.remove("fv-caught");
        card?.classList.remove("fv-card-caught");
      }, 850);
      document.dispatchEvent(new Event("flexvibe:cart-open"));
    } catch (error) {
      alert(error.message || "Could not add item. Please try again.");
    }

    add.disabled = false;
    add.textContent = add.dataset.label || "Add to cart";
    return;
  }

  const cartQuantity = event.target.closest("[data-cart-quantity]");
  if (cartQuantity) {
    event.preventDefault();
    cartQuantity.disabled = true;
    try {
      const response = await fetch(`${fvRoutes.cartChange || "/cart/change"}.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ id: cartQuantity.dataset.lineKey, quantity: Number(cartQuantity.dataset.cartQuantity) }),
      });
      if (!response.ok) throw new Error("Unable to update cart");
      await refreshCart();
      const status = document.querySelector("[data-cart-status]");
      if (status) status.textContent = "Cart updated";
    } catch (error) {
      alert(error.message || "Could not update cart.");
      cartQuantity.disabled = false;
    }
    return;
  }

  const minus = event.target.closest("[data-qty-minus]");
  const plus = event.target.closest("[data-qty-plus]");

  if (minus || plus) {
    event.preventDefault();

    const wrapper = event.target.closest(".fv-qty");

    if (!wrapper) return;

    const input = wrapper.querySelector("input");

    if (!input) return;

    let value = parseInt(input.value || "1", 10);

    if (plus) value += 1;
    if (minus) value = Math.max(1, value - 1);

    input.value = value;
  }
});

document.addEventListener("DOMContentLoaded", () => {
  refreshCart();

  document.querySelectorAll("[data-faq]").forEach((faq) => {
    const search = faq.querySelector("[data-faq-search]");
    const filters = [...faq.querySelectorAll("[data-faq-filter]")];
    const items = [...faq.querySelectorAll("[data-faq-item]")];
    const empty = faq.querySelector("[data-faq-empty]");
    let category = "all";

    const updateFaq = () => {
      const query = (search?.value || "").trim().toLowerCase();
      let visible = 0;
      items.forEach((item) => {
        const matchesCategory = category === "all" || item.dataset.faqCategory === category;
        const matchesQuery = !query || item.textContent.toLowerCase().includes(query);
        item.hidden = !(matchesCategory && matchesQuery);
        if (!item.hidden) visible += 1;
      });
      if (empty) empty.hidden = visible !== 0;
    };

    search?.addEventListener("input", updateFaq);
    filters.forEach((filter) => filter.addEventListener("click", () => {
      category = filter.dataset.faqFilter || "all";
      filters.forEach((button) => button.classList.toggle("is-active", button === filter));
      updateFaq();
    }));
  });

  const ageNote = document.querySelector("[data-age-note]");
  if (ageNote && localStorage.getItem("flexvibe-age-note-dismissed") !== "true") {
    ageNote.hidden = false;
  }

  const grid = document.getElementById("ProductDatabase");

  if (grid) {
    const search = document.getElementById("DbSearch");
    const type = document.getElementById("DbType");
    const series = document.getElementById("DbSeries");
    const cards = [...grid.querySelectorAll(".db-card")];

    if (series) {
      const seriesValues = [...new Set(cards.map((card) => card.dataset.series).filter(Boolean))].sort();

      seriesValues.forEach((value) => {
        const option = document.createElement("option");
        option.value = value;
        option.textContent = value.replace(/\b\w/g, (match) => match.toUpperCase());
        series.appendChild(option);
      });
    }

    const filter = () => {
      const query = (search?.value || "").toLowerCase().trim();
      const selectedType = type?.value || "all";
      const selectedSeries = series?.value || "all";

      cards.forEach((card) => {
        const haystack = [
          card.dataset.title,
          card.dataset.series,
          card.dataset.type,
          card.dataset.tags,
        ]
          .join(" ")
          .toLowerCase();

        const matchesQuery = !query || haystack.includes(query);
        const matchesType = selectedType === "all" || (card.dataset.type || "").includes(selectedType);
        const matchesSeries = selectedSeries === "all" || card.dataset.series === selectedSeries;

        card.style.display = matchesQuery && matchesType && matchesSeries ? "" : "none";
      });
    };

    [search, type, series].forEach((element) => {
      if (element) element.addEventListener("input", filter);
    });
  }

  const mainImage = document.getElementById("fvMainImage");

if(mainImage){

    document.querySelectorAll(".fv-thumb").forEach((thumb)=>{

        thumb.addEventListener("click",()=>{

            document
            .querySelectorAll(".fv-thumb")
            .forEach(t=>t.classList.remove("active"));

            thumb.classList.add("active");

            mainImage.src = thumb.dataset.image;

        });

    });

}
});
document.addEventListener("click", (event) => {
  const button = event.target.closest("[data-tab]");

  if (!button) return;

  const tabs = button.closest(".fv-product-tabs");

  if (!tabs) return;

  const target = button.dataset.tab;

  tabs.querySelectorAll("[data-tab]").forEach((btn) => {
    btn.classList.remove("is-active");
  });

  tabs.querySelectorAll("[data-tab-panel]").forEach((panel) => {
    panel.classList.remove("is-active");
  });

  button.classList.add("is-active");

  const activePanel = tabs.querySelector(`[data-tab-panel="${target}"]`);

  if (activePanel) {
    activePanel.classList.add("is-active");
  }
});
document.addEventListener("click", (event) => {
  const open = event.target.closest("[data-open-gallery]");
  const close = event.target.closest("[data-close-gallery]");
  const lightbox = document.querySelector("[data-gallery-lightbox]");
  const main = document.getElementById("fvMainImage");
  const lightboxImage = document.getElementById("fvLightboxImage");

  if (open && lightbox) {
    event.preventDefault();

    if (main && lightboxImage) {
      lightboxImage.src = main.src;
    }

    lightbox.hidden = false;
    document.body.style.overflow = "hidden";
  }

  if (close && lightbox) {
    event.preventDefault();
    lightbox.hidden = true;
    document.body.style.overflow = "";
  }
});

document.addEventListener("keydown", (event) => {
  if (event.key !== "Escape") return;

  const lightbox = document.querySelector("[data-gallery-lightbox]");
  if (!lightbox || lightbox.hidden) return;

  lightbox.hidden = true;
  document.body.style.overflow = "";
});
document.addEventListener("click", function (event) {
  const button = event.target.closest("[data-open-qv]");
  const closeButton = event.target.closest("[data-close-quick]");
  const modal = document.getElementById("FlexVibeQuickView");
  const thumbnail = event.target.closest("[data-qv-thumbnail]");

  if (thumbnail && modal) {
    event.preventDefault();
    const mainImage = modal.querySelector("[data-qv-main-image]");
    if (mainImage && thumbnail.dataset.image) {
      mainImage.classList.remove("is-switching");
      mainImage.src = thumbnail.dataset.image;
      requestAnimationFrame(() => mainImage.classList.add("is-switching"));
      modal.querySelectorAll("[data-qv-thumbnail]").forEach((item) => item.classList.toggle("is-active", item === thumbnail));
    }
    return;
  }

  if (button) {
    event.preventDefault();

    if (!modal) {
      console.warn("Quick View modal not found");
      return;
    }

    const title = modal.querySelector("[data-qv-title]");
    const price = modal.querySelector("[data-qv-price]");
    const vendor = modal.querySelector("[data-qv-vendor]");
    const desc = modal.querySelector("[data-qv-description]");
    const link = modal.querySelector("[data-qv-link]");
    const imageBox = modal.querySelector("[data-qv-image]");
    const status = modal.querySelector("[data-qv-status]");
    const facts = modal.querySelector("[data-qv-facts]");
    const form = modal.querySelector("[data-qv-form]");
    const addButton = form?.querySelector("[data-ajax-add]");
    const available = button.dataset.available === "true";

    if (title) title.textContent = button.dataset.title || "";
    if (price) price.textContent = button.dataset.price || "";
    if (vendor) vendor.textContent = button.dataset.vendor || "";
    if (desc) desc.textContent = button.dataset.description || "View the full product page for complete collector details, condition notes, and specifications.";
    if (link) link.href = button.dataset.url || "#";
    const variantInput = modal.querySelector("[data-qv-variant-id]");
    if (variantInput) variantInput.value = button.dataset.variantId || "";
    if (status) {
      status.textContent = available ? "In stock" : "Sold out";
      status.classList.toggle("is-available", available);
    }
    if (form) form.hidden = !available;
    if (addButton) addButton.disabled = !available;

    if (facts) {
      const productFacts = [
        ["Type", button.dataset.productType],
        ["Game", button.dataset.game],
        ["Set", button.dataset.set],
        ["Condition", button.dataset.condition]
      ].filter(([, value]) => value);
      facts.innerHTML = productFacts.map(([label, value]) =>
        `<span><small>${fvEscapeHtml(label)}</small><strong>${fvEscapeHtml(value)}</strong></span>`
      ).join("");
      facts.hidden = productFacts.length === 0;
    }

    if (imageBox) {
      const images = [button.dataset.image, button.dataset.image2, button.dataset.image3, button.dataset.image4].filter(Boolean);
      imageBox.innerHTML = images.length
        ? `<div class="fv-qv-main-media"><img src="${fvEscapeHtml(images[0])}" alt="${fvEscapeHtml(button.dataset.title || "")}" class="fv-qv-img" data-qv-main-image></div>${images.length > 1 ? `<div class="fv-qv-thumbnails" aria-label="Product images">${images.map((image, index) => `<button type="button" data-qv-thumbnail data-image="${fvEscapeHtml(image)}" class="${index === 0 ? "is-active" : ""}" aria-label="View product image ${index + 1}"><img src="${fvEscapeHtml(image)}" alt="" loading="lazy"></button>`).join("")}</div>` : ""}`
        : `<div class="fv-qv-image-fallback"><span>FV</span><small>Product image coming soon</small></div>`;
    }

    modal.hidden = false;
    modal.querySelector(".fv-quick-card")?.classList.remove("is-entering");
    requestAnimationFrame(() => modal.querySelector(".fv-quick-card")?.classList.add("is-entering"));
    document.body.style.overflow = "hidden";
    modal.querySelector("[data-close-quick]")?.focus();
    return;
  }

  if (closeButton) {
    event.preventDefault();

    if (modal) {
      modal.hidden = true;
      document.body.style.overflow = "";
    }
  }
});

document.addEventListener("click", (event) => {
  const dismiss = event.target.closest("[data-dismiss-age-note]");
  if (!dismiss) return;
  localStorage.setItem("flexvibe-age-note-dismissed", "true");
  dismiss.closest("[data-age-note]")?.remove();
});

document.addEventListener("change", (event) => {
  const select = event.target.closest("[data-variant-select]");
  if (!select) return;
  const option = select.selectedOptions[0];
  const form = select.closest("form");
  const price = select.closest(".fv-buy-box")?.querySelector("[data-product-price]");
  const addButton = form?.querySelector("[data-ajax-add]");
  const currentPrice = price?.querySelector("[data-current-price]");
  const comparePrice = price?.querySelector("[data-compare-price]");
  if (currentPrice && option?.dataset.price) currentPrice.textContent = option.dataset.price;
  if (comparePrice) {
    comparePrice.textContent = option?.dataset.comparePrice || "";
    comparePrice.hidden = !option?.dataset.comparePrice;
  }
  if (addButton) {
    const available = option?.dataset.available === "true";
    addButton.disabled = !available;
    addButton.textContent = available ? addButton.dataset.label || "Add to Cart" : "Sold Out";
  }
  const url = new URL(window.location.href);
  url.searchParams.set("variant", select.value);
  window.history.replaceState({}, "", url);
});

/* ==========================
   FLEXVIBE SEARCH OVERLAY
========================== */

document.addEventListener("click", (event) => {

  const overlay = document.getElementById("FlexSearch");

  if (!overlay) return;

  // Open Search
  if (event.target.closest("[data-open-search]")) {
    event.preventDefault();

    overlay.hidden = false;
    document.body.style.overflow = "hidden";

    const input = document.getElementById("FlexSearchInput");

    if (input) {
      setTimeout(() => input.focus(), 100);
    }

    return;
  }

  // Close Search
  if (event.target.closest("[data-close-search]")) {
    overlay.hidden = true;
    document.body.style.overflow = "";
    return;
  }

});
document.addEventListener("keydown", (event) => {

  if (event.key !== "Escape") return;

  const overlay = document.getElementById("FlexSearch");

  if (!overlay || overlay.hidden) return;

  overlay.hidden = true;
  document.body.style.overflow = "";

});
/* ==========================
   FLEXVIBE PREDICTIVE SEARCH
========================== */

let fvSearchTimer;
let fvSearchController;

document.addEventListener("input", async (event) => {
  const input = event.target.closest("#FlexSearchInput");

  if (!input) return;

  const resultsBox = document.getElementById("FlexSearchResults");
  const query = input.value.trim();

  if (!resultsBox) return;

  if (query.length < 2) {
    clearTimeout(fvSearchTimer);
    fvSearchController?.abort();
    const defaultSearch = document.getElementById("FlexSearchDefault");
    resultsBox.innerHTML = defaultSearch?.innerHTML || `<div class="fv-search-empty">Start typing to search...</div>`;
    return;
  }

  clearTimeout(fvSearchTimer);
  await new Promise((resolve) => { fvSearchTimer = setTimeout(resolve, 180); });
  if (input.value.trim() !== query) return;

  fvSearchController?.abort();
  fvSearchController = new AbortController();
  resultsBox.innerHTML = `<div class="fv-search-empty">Searching...</div>`;

  try {
    const response = await fetch(
      `${fvRoutes.predictiveSearch || "/search/suggest"}.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page&resources[limit]=6`,
      { signal: fvSearchController.signal }
    );

    if (!response.ok) throw new Error(`Search failed: ${response.status}`);

    const data = await response.json();

    const products = data.resources?.results?.products || [];
    const collections = data.resources?.results?.collections || [];
    const pages = data.resources?.results?.pages || [];

    if (!products.length && !collections.length && !pages.length) {
      resultsBox.innerHTML = `<div class="fv-search-empty">No results found.</div>`;
      return;
    }

    let html = "";

    if (products.length) {
      html += `<div class="fv-search-group"><h3>Products</h3><div class="fv-search-results-grid">`;

      products.forEach((product) => {
        html += `
            <a class="fv-search-result-card" href="${fvEscapeHtml(product.url)}">
            <div class="fv-search-result-img">
              ${
                product.image
                  ? `<img src="${fvEscapeHtml(product.image)}" alt="${fvEscapeHtml(product.title)}">`
                  : `<span>FV</span>`
              }
            </div>
            <div>
              <strong>${fvEscapeHtml(product.title)}</strong>
              <small>${fvEscapeHtml(product.price || "")}</small>
            </div>
          </a>
        `;
      });

      html += `</div></div>`;
    }

    if (collections.length) {
      html += `<div class="fv-search-group"><h3>Collections</h3>`;

      collections.forEach((collection) => {
        html += `<a class="fv-search-link" href="${fvEscapeHtml(collection.url)}">${fvEscapeHtml(collection.title)}</a>`;
      });

      html += `</div>`;
    }

    if (pages.length) {
      html += `<div class="fv-search-group"><h3>Pages</h3>`;

      pages.forEach((page) => {
        html += `<a class="fv-search-link" href="${fvEscapeHtml(page.url)}">${fvEscapeHtml(page.title)}</a>`;
      });

      html += `</div>`;
    }

    resultsBox.innerHTML = html;
  } catch (error) {
    if (error.name === "AbortError") return;
    resultsBox.innerHTML = `<div class="fv-search-empty">Search temporarily unavailable.</div>`;
  }
});
document.addEventListener("click", (event) => {
  const open = event.target.closest("[data-open-mobile-menu]");
  const close = event.target.closest("[data-close-mobile-menu]");
  const drawer = document.querySelector("[data-mobile-drawer]");

  if (!drawer) return;

  if (open) {
    event.preventDefault();
    drawer.hidden = false;
    document.body.style.overflow = "hidden";
  }

  if (close) {
    event.preventDefault();
    drawer.hidden = true;
    document.body.style.overflow = "";
  }
});
/* =====================================================
   FLEXVIBE WISHLIST
===================================================== */

const FV_WISHLIST_KEY = "flexvibe-wishlist";

function fvGetWishlist() {
  try {
    const items = JSON.parse(localStorage.getItem(FV_WISHLIST_KEY) || "[]");
    return Array.isArray(items) ? items : [];
  } catch (error) {
    return [];
  }
}

function fvSaveWishlist(items) {
  localStorage.setItem(FV_WISHLIST_KEY, JSON.stringify(items));
  fvUpdateWishlistCounter();
  fvPaintWishlistButtons();
}

function fvUpdateWishlistCounter() {
  const count = fvGetWishlist().length;

  document.querySelectorAll("[data-wishlist-count]").forEach(el => {
    el.textContent = count;

    if (count === 0) {
      el.style.display = "none";
    } else {
      el.style.display = "grid";
    }
  });
}

function fvPaintWishlistButtons() {

  const wishlist = fvGetWishlist();

  document.querySelectorAll("[data-wishlist]").forEach(btn => {

    const saved = wishlist.some(item => item.id == btn.dataset.productId);

    btn.classList.toggle("saved", saved);

    btn.innerHTML = saved ? "❤" : "♡";

  });

}

document.addEventListener("click", e => {

  const btn = e.target.closest("[data-wishlist]");

  if (!btn) return;

  let wishlist = fvGetWishlist();

  const id = btn.dataset.productId;

  const exists = wishlist.find(item => item.id == id);

  if (exists) {

    wishlist = wishlist.filter(item => item.id != id);

  } else {

    wishlist.push({

      id: id,
      title: btn.dataset.productTitle,
      url: btn.dataset.productUrl,
      image: btn.dataset.productImage,
      price: btn.dataset.productPrice

    });

  }

  fvSaveWishlist(wishlist);

});

document.addEventListener("DOMContentLoaded", () => {

  fvUpdateWishlistCounter();

  fvPaintWishlistButtons();

});
/* =====================================================
   FLEXVIBE WISHLIST PAGE
===================================================== */

function fvRenderWishlistPage() {
  const page = document.getElementById("FlexWishlistPage");
  if (!page) return;

  const items = fvGetWishlist();
  const pageCount = document.querySelector("[data-wishlist-page-count]");
  if (pageCount) pageCount.textContent = items.length;

  if (!items.length) {
    page.innerHTML = `
      <div class="glass fv-empty-wishlist">
        <div class="fv-empty-vault" aria-hidden="true">
          <span class="fv-empty-heart">♡</span>
          <i></i><i></i><i></i>
        </div>
        <div class="fv-empty-copy">
          <span class="eyebrow">YOUR VAULT IS READY</span>
          <h2>Save your next chase.</h2>
          <p>Tap the heart on sealed products, singles, or graded cards to keep every potential pickup together.</p>
          <div class="fv-empty-actions">
            <a href="/collections/all" class="btn">Explore all products</a>
            <a href="/collections/graded-cards" class="btn secondary">Browse graded cards</a>
          </div>
        </div>
        <div class="fv-wishlist-benefits">
          <span><strong>♡</strong> Save collector picks</span>
          <span><strong>↗</strong> Return in one tap</span>
          <span><strong>⚡</strong> Watch for restocks</span>
        </div>
      </div>
    `;
    return;
  }

  page.innerHTML = `
    <div class="fv-wishlist-grid">
      ${items.map(item => `
        <div class="fv-card glass">
          <a href="${fvEscapeHtml(item.url)}" class="fv-card-image">
            ${item.image
              ? `<img class="fv-card-img" src="${fvEscapeHtml(item.image)}" alt="${fvEscapeHtml(item.title)}">`
              : `<div class="fv-card-fallback">FV</div>`}
          </a>

          <div class="fv-card-body">
            <h3><a href="${fvEscapeHtml(item.url)}">${fvEscapeHtml(item.title)}</a></h3>
            <div class="fv-price">${fvEscapeHtml(item.price)}</div>

            <a class="btn full" href="${fvEscapeHtml(item.url)}">View Product</a>

            <button
              class="btn secondary full"
              data-remove-wishlist-page
              data-product-id="${item.id}">
              Remove
            </button>
          </div>
        </div>
      `).join("")}
    </div>
  `;
}

document.addEventListener("DOMContentLoaded", fvRenderWishlistPage);

document.addEventListener("click", (event) => {
  const remove = event.target.closest("[data-remove-wishlist-page]");
  if (!remove) return;

  const id = remove.dataset.productId;
  const items = fvGetWishlist().filter(item => item.id != id);

  fvSaveWishlist(items);
  fvRenderWishlistPage();
});
/* RECENTLY VIEWED PRODUCTS */

const FV_RECENT_KEY = "flexvibe-recently-viewed";

function fvGetRecentlyViewed() {
  try {
    const items = JSON.parse(localStorage.getItem(FV_RECENT_KEY) || "[]");
    return Array.isArray(items) ? items : [];
  } catch (error) {
    return [];
  }
}

function fvSaveRecentlyViewed(items) {
  localStorage.setItem(FV_RECENT_KEY, JSON.stringify(items.slice(0, 8)));
}

function fvTrackCurrentProduct() {
  const title = document.querySelector(".fv-buy-box h1")?.textContent?.trim();
  const price = document.querySelector(".fv-buy-box .price")?.textContent?.trim();
  const image = document.querySelector("#fvMainImage")?.src;
  const url = window.location.pathname;

  if (!title || !url.includes("/products/")) return;

  let items = fvGetRecentlyViewed();

  items = items.filter(item => item.url !== url);

  items.unshift({
    title,
    price,
    image,
    url
  });

  fvSaveRecentlyViewed(items);
}

function fvRenderRecentlyViewed() {
  const container = document.getElementById("FlexRecentlyViewed");
  if (!container) return;

  const currentUrl = window.location.pathname;

  const items = fvGetRecentlyViewed()
    .filter(item => item.url !== currentUrl)
    .slice(0, 4);

  if (!items.length) {
    container.closest(".fv-recently-viewed").style.display = "none";
    return;
  }

  container.innerHTML = items.map(item => `
    <div class="fv-card glass">
      <a href="${fvEscapeHtml(item.url)}" class="fv-card-image">
        ${
          item.image
            ? `<img class="fv-card-img" src="${fvEscapeHtml(item.image)}" alt="${fvEscapeHtml(item.title)}">`
            : `<div class="fv-card-fallback">FV</div>`
        }
      </a>

      <div class="fv-card-body">
        <h3><a href="${fvEscapeHtml(item.url)}">${fvEscapeHtml(item.title)}</a></h3>
        <div class="fv-price">${fvEscapeHtml(item.price || "")}</div>
        <a href="${fvEscapeHtml(item.url)}" class="btn full">View Product</a>
      </div>
    </div>
  `).join("");
}

document.addEventListener("DOMContentLoaded", () => {
  fvTrackCurrentProduct();
  fvRenderRecentlyViewed();
});
/* ==========================================
   PREORDER COUNTDOWN
========================================== */

function startReleaseCountdown() {

    const box = document.querySelector(".fv-countdown");

    if (!box) return;

    const target = new Date(box.dataset.releaseDate);

    function update() {

        const now = new Date();
        const diff = target - now;

        if (diff <= 0) {

            box.innerHTML = `
                <div class="fv-countdown-title">
                    AVAILABLE NOW
                </div>

                <div class="fv-countdown-time">
                    🎉 Released
                </div>
            `;

            return;
        }

        const days = Math.floor(diff / 86400000);
        const hours = Math.floor((diff % 86400000) / 3600000);
        const mins = Math.floor((diff % 3600000) / 60000);

        box.innerHTML = `
            <div class="fv-countdown-title">
                Releases In
            </div>

            <div class="fv-countdown-time">
                ${days}d ${hours}h ${mins}m
            </div>

            <div class="fv-countdown-small">
                Guaranteed allocation while supplies last.
            </div>
        `;
    }

    update();
    setInterval(update, 60000);
}

document.addEventListener("DOMContentLoaded", startReleaseCountdown);

/* ==========================================
   STOREFRONT MOTION AND INTERACTION
========================================== */

function initFlexVibeMotion() {
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduceMotion) return;

  document.documentElement.classList.add("fv-motion-enabled");

  document.querySelectorAll(".fv-hero-v3, .fv-product-visual, .fv-collection-hero").forEach((surface) => {
    if (surface.querySelector(".fv-energy-field")) return;
    const field = document.createElement("span");
    field.className = "fv-energy-field";
    field.setAttribute("aria-hidden", "true");
    for (let index = 0; index < 7; index += 1) {
      const mote = document.createElement("i");
      mote.style.setProperty("--mote", index);
      field.appendChild(mote);
    }
    surface.prepend(field);
  });

  const revealTargets = document.querySelectorAll(
    ".section, .fv-set-section, .fv-product-page, .fv-collection-hero, .page-content > .page-width, .footer-grid"
  );

  if ("IntersectionObserver" in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: "0px 0px -40px" });

    revealTargets.forEach((element, index) => {
      element.classList.add("fv-reveal");
      element.style.setProperty("--fv-reveal-delay", `${Math.min(index % 4, 3) * 70}ms`);
      observer.observe(element);
    });
  }

  if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

  document.querySelectorAll(".fv-card, .collection-tile, .fv-set-card-v2").forEach((card) => {
    card.classList.add("fv-interactive-card");

    card.addEventListener("pointermove", (event) => {
      const rect = card.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      card.style.setProperty("--fv-pointer-x", `${x * 100}%`);
      card.style.setProperty("--fv-pointer-y", `${y * 100}%`);
      card.style.setProperty("--fv-rotate-x", `${(0.5 - y) * 5}deg`);
      card.style.setProperty("--fv-rotate-y", `${(x - 0.5) * 6}deg`);
    });

    card.addEventListener("pointerleave", () => {
      card.style.removeProperty("--fv-rotate-x");
      card.style.removeProperty("--fv-rotate-y");
    });
  });

  const hero = document.querySelector(".fv-hero-v3");
  const heroCard = hero?.querySelector(".fv-hero-v3-card");
  if (hero && heroCard) {
    hero.addEventListener("pointermove", (event) => {
      const rect = hero.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width - 0.5;
      const y = (event.clientY - rect.top) / rect.height - 0.5;
      heroCard.style.setProperty("--fv-hero-x", `${x * 12}px`);
      heroCard.style.setProperty("--fv-hero-y", `${y * 10}px`);
    });
    hero.addEventListener("pointerleave", () => {
      heroCard.style.removeProperty("--fv-hero-x");
      heroCard.style.removeProperty("--fv-hero-y");
    });
  }
}

document.addEventListener("DOMContentLoaded", initFlexVibeMotion);

function initFlexVibePageMotion() {
  const progress = document.querySelector("[data-scroll-progress]");
  const header = document.querySelector(".fv-header");
  let ticking = false;

  const update = () => {
    const scrollable = document.documentElement.scrollHeight - window.innerHeight;
    const percent = scrollable > 0 ? Math.min(100, (window.scrollY / scrollable) * 100) : 0;
    if (progress) progress.style.width = `${percent}%`;
    if (header) header.classList.toggle("is-scrolled", window.scrollY > 24);
    ticking = false;
  };

  window.addEventListener("scroll", () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }, { passive: true });

  update();

  document.addEventListener("pointerdown", (event) => {
    const target = event.target.closest(".btn, .fv-btn-main, .fv-btn-outline, .fv-header-icon");
    if (!target || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const rect = target.getBoundingClientRect();
    target.style.setProperty("--fv-click-x", `${event.clientX - rect.left}px`);
    target.style.setProperty("--fv-click-y", `${event.clientY - rect.top}px`);
    target.classList.remove("fv-clicked");
    requestAnimationFrame(() => target.classList.add("fv-clicked"));
  });
}

document.addEventListener("DOMContentLoaded", initFlexVibePageMotion);
